import { ClientWorkspaceOverview } from "apps/project-iq/components/ClientWorkspaceOverview/ClientWorkspaceOverview";
import { WorkspaceClientInvitations } from "apps/project-iq/components/WorkspaceClientInvitations/WorkspaceClientInvitations";
import { WorkspaceChat } from "apps/project-iq/components/WorkspaceChat/WorkspaceChat";
import { WorkspaceDiagnostics } from "apps/project-iq/components/WorkspaceDiagnostics/WorkspaceDiagnostics";
import { WorkspaceFiles } from "apps/project-iq/components/WorkspaceFiles/WorkspaceFiles";
import { WorkspaceOverview } from "apps/project-iq/components/WorkspaceOverview/WorkspaceOverview";
import { WorkspacePeople } from "apps/project-iq/components/WorkspacePeople/WorkspacePeople";
import { ProjectReview } from "apps/project-iq/components/ProjectReview/ProjectReview";
import { WorkspacePlan } from "apps/project-iq/components/WorkspacePlan/WorkspacePlan";
import { WorkspaceAnalytics } from "apps/project-iq/components/WorkspaceAnalytics/WorkspaceAnalytics";
import { CheckmarkThinIcon } from "apps/project-iq/components/icons/ProjectIqIcons";
import { ClientDeliverablesPage } from "apps/project-iq/pages/ClientDeliverablesPage/ClientDeliverablesPage";
import { QualityReviewPage } from "apps/project-iq/pages/QualityReviewPage/QualityReviewPage";
import { VaultPage } from "apps/project-iq/pages/VaultPage/VaultPage";
import { ValuePage } from "apps/project-iq/pages/ValuePage/ValuePage";
import { RetrospectivePage } from "apps/project-iq/pages/RetrospectivePage/RetrospectivePage";
import { ValueStoryPage } from "apps/project-iq/pages/ValueStoryPage/ValueStoryPage";
import { ITabDescriptor, ITabSharedProps } from "./workspaceTabRegistry.types";

export type { ITabDescriptor, ITabSharedProps };

export const WORKSPACE_TAB_REGISTRY: Record<string, ITabDescriptor> = {
  chat: {
    isFullContent: true,
    render: (props) => (
      <WorkspaceChat
        workspaceId={props.workspaceId}
        workspaceName={props.workspaceName}
        workspaceCode={props.workspaceCode}
        onConversationOpenChange={props.onChatConversationOpenChange}
      />
    ),
  },
  files: {
    isFullContent: true,
    render: (props) => (
      <WorkspaceFiles
        workspaceId={props.workspaceId}
        clientName={props.clientName}
        sharepointConnections={props.workspace.sharepointConnections}
        onSaveStatusChange={props.onDocumentsSaveStatusChange}
      />
    ),
    headerAction: (props) => {
      if (props.documentsSaveStatus === "saving") {
        return (
          <span className={props.planSaveIndicatorClass}>
            {props.documentsSaveStatusLabel ?? "Saving…"}
          </span>
        );
      }
      if (props.documentsSaveStatus === "saved") {
        return (
          <span className={props.planSaveIndicatorClass}>
            <CheckmarkThinIcon size={15} aria-hidden="true" />
            {props.documentsSaveStatusLabel ?? "Saved"}
          </span>
        );
      }
      if (props.documentsSaveStatus === "error") {
        return <span className={props.planSaveErrorClass}>Failed to save</span>;
      }
      return undefined;
    },
  },
  vault: {
    isFullContent: true,
    render: (props) => (
      <VaultPage
        workspaceCode={props.workspaceCode}
        clientName={props.clientName}
      />
    ),
  },
  overview: {
    isFullContent: true,
    render: (props) => (
      <WorkspaceOverview
        workspace={props.workspace}
        onNavigateToTab={props.onNavigateToTab}
      />
    ),
  },
  "client-overview": {
    isFullContent: true,
    render: (props) => <ClientWorkspaceOverview workspace={props.workspace} />,
  },
  diagnostics: {
    isFullContent: false,
    render: (props) => <WorkspaceDiagnostics workspaceId={props.workspaceId} />,
  },
  "nh-diagnostic-iq": {
    isFullContent: true,
    render: (props) => <WorkspaceDiagnostics workspaceId={props.workspaceId} />,
  },
  "ai-analytics": {
    isFullContent: true,
    render: (props) => (
      <WorkspaceAnalytics
        workspaceId={props.workspaceId}
        workspace={props.workspace}
      />
    ),
  },
  plan: {
    isFullContent: true,
    render: (props) => (
      <WorkspacePlan
        workspaceId={props.workspaceId}
        onSaveStatusChange={props.onSaveStatusChange}
        initialTab={props.planInitialTab}
      />
    ),
    headerAction: (props) => {
      if (props.planSaveStatus === "saving") {
        return <span className={props.planSaveIndicatorClass}>Saving…</span>;
      }
      if (props.planSaveStatus === "saved") {
        return (
          <span className={props.planSaveIndicatorClass}>
            <CheckmarkThinIcon size={15} aria-hidden="true" />
            Saved
          </span>
        );
      }
      if (props.planSaveStatus === "error") {
        return <span className={props.planSaveErrorClass}>Failed to save</span>;
      }
      return undefined;
    },
  },
  "value-story": {
    isFullContent: true,
    render: () => <ValueStoryPage />,
  },
  retrospective: {
    isFullContent: true,
    render: (props) => <RetrospectivePage workspaceId={props.workspaceId} />,
  },
  people: {
    isFullContent: true,
    render: (props) => (
      <WorkspacePeople
        workspaceIdentifier={props.workspaceId}
        onSaveStatusChange={props.onPeopleSaveStatusChange}
      />
    ),
    headerAction: (props) => {
      if (props.peopleSaveStatus === "saving") {
        return <span className={props.planSaveIndicatorClass}>Saving…</span>;
      }
      if (props.peopleSaveStatus === "saved") {
        return (
          <span className={props.planSaveIndicatorClass}>
            <CheckmarkThinIcon size={15} aria-hidden="true" />
            Saved
          </span>
        );
      }
      if (props.peopleSaveStatus === "error") {
        return <span className={props.planSaveErrorClass}>Failed to save</span>;
      }
      return undefined;
    },
  },
  value: {
    isFullContent: true,
    render: (props) => (
      <ValuePage
        workspaceIdentifier={props.workspaceId}
        onSaveStatusChange={props.onSaveStatusChange}
      />
    ),
  },
  "quality-review": {
    isFullContent: true,
    render: (props) => <QualityReviewPage workspaceId={props.workspaceId} />,
  },
  "project-reviews": {
    isFullContent: true,
    render: (props) => (
      <ProjectReview
        workspaceIdentifier={props.workspaceId}
        onSaveStatusChange={props.onProjectReviewSaveStatusChange}
      />
    ),
    headerAction: (props) => {
      if (props.projectReviewSaveStatus === "saving") {
        return <span className={props.planSaveIndicatorClass}>Saving…</span>;
      }
      if (props.projectReviewSaveStatus === "saved") {
        return (
          <span className={props.planSaveIndicatorClass}>
            <CheckmarkThinIcon size={15} aria-hidden="true" />
            Saved
          </span>
        );
      }
      if (props.projectReviewSaveStatus === "error") {
        return <span className={props.planSaveErrorClass}>Failed to save</span>;
      }
      return undefined;
    },
  },
  "client-deliverables": {
    isFullContent: true,
    render: (props) => (
      <ClientDeliverablesPage workspaceId={props.workspaceId} />
    ),
  },
  "client-invitations": {
    isFullContent: true,
    render: (props) => (
      <WorkspaceClientInvitations workspaceId={props.workspaceId} />
    ),
  },
};
